import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, ArrowUpRight, TrendingUp, KeyRound, CreditCard, Zap, FlaskConical,
  ChevronRight, Sparkles, AlertTriangle, CheckCircle2, Clock, MessageCircle,
  Cake, Sun, Moon, Sunset, Coffee, BookOpen, Lightbulb, Quote, Heart,
} from 'lucide-react';
import { Card } from '../components/Card';
import { SectionHeader } from '../components/Tabs';
import { Badge, Dot } from '../components/Badge';
import { Button } from '../components/Button';
import { AreaChart } from '../components/AreaChart';
import { DonutChart } from '../components/DonutChart';
import { Skeleton, EmptyState } from '../components/Dialog';
import RenewalBanner from '../components/RenewalBanner';
import { apiFetch } from '../api';
import { cn, formatCompact, formatNumber, formatRelative } from '../lib/utils';

// ---------------------------------------------------------------------------
// Daily spark — rotating cozy tips/affirmations. Deterministic by day of
// year so the same tip is shown for the whole day (and re-shuffles tomorrow).
// No backend call, no random flicker on re-render — pure client-side.
// ---------------------------------------------------------------------------
const SPARKS = [
  { icon: Sun,        text: 'Cache your repeats — most calls hit the same 20% of URLs.' },
  { icon: Coffee,     text: 'Take a breath. The proxy pool warms up after a few warm calls.' },
  { icon: Lightbulb,  text: 'A 500 with `cf_cached: true` is fine. It just means Cloudflare handled it.' },
  { icon: BookOpen,   text: 'Rotating a key takes 5 seconds. Doing it every quarter is good hygiene.' },
  { icon: Sparkles,   text: 'The Playground runs against real Cloudflare — it counts as a real call.' },
  { icon: Quote,      text: '"If you can scrape it once, you can scrape it forever." — old Datames proverb' },
  { icon: Zap,        text: 'Use `output: stealth` only on domains that bounce. Classic is half the cost.' },
  { icon: Heart,      text: 'You have used ScraperArgus today. That is enough.' },
  { icon: Cake,       text: 'Small dataset, big result. Iteration beats volume.' },
  { icon: Clock,      text: 'Most scrapes finish in under 2 seconds. The ones that don\'t usually need JS render.' },
  { icon: Sun,        text: 'Your future self will thank you for naming your keys well.' },
  { icon: Moon,       text: 'The API never sleeps, but you should. Dormant keys auto-revoke after 90 days.' },
  { icon: Sunset,     text: 'Sunset reminder: top-up is unlocked the moment your cycle hits 0.' },
  { icon: Lightbulb,  text: 'Browser render is roughly 5× the cost of a classic fetch. Plan accordingly.' },
];

function getDailySpark(userCreatedAt) {
  // Hash (today + created_at) → stable pick for the day, slightly personalized
  const now = new Date();
  const ymd = `${now.getUTCFullYear()}-${now.getUTCMonth()}-${now.getUTCDate()}`;
  const seed = (ymd + (userCreatedAt || '')).split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return SPARKS[seed % SPARKS.length];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getFullName(user) {
  if (!user) return '';
  const meta = user.user_metadata || {};
  if (meta.display_name && meta.display_name.trim()) return meta.display_name.trim();
  const first = (meta.first_name || '').trim();
  const last = (meta.last_name || '').trim();
  if (first || last) return `${first} ${last}`.trim();
  if (meta.full_name && meta.full_name.trim()) return meta.full_name.trim();
  if (meta.name && meta.name.trim()) return meta.name.trim();
  if (user.email && user.email.includes('@')) {
    return user.email.split('@')[0].replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return '';
}

function getTimeGreeting(date = new Date()) {
  const h = date.getHours();
  if (h >= 5 && h < 12) return { greeting: 'Good morning', emoji: '☀️' };
  if (h >= 12 && h < 17) return { greeting: 'Good afternoon', emoji: '🌤️' };
  if (h >= 17 && h < 22) return { greeting: 'Good evening', emoji: '🌙' };
  return { greeting: 'Up late', emoji: '🌙' };
}

function formatLongDate(date = new Date()) {
  return date.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Hero({ user, billing, loading }) {
  const fullName = getFullName(user);
  const { greeting, emoji } = getTimeGreeting();
  const credits = billing?.credit_balance ?? 0;
  const refill = billing?.initial_credits ?? 0;
  const hasRefill = refill > 0;
  const isLow = hasRefill && credits / refill < 0.2;
  const isEmpty = credits === 0;

  return (
    <div className="relative overflow-hidden rounded-xl border border-border bg-gradient-to-br from-brand/8 via-surface to-surface-2">
      {/* Warm gradient orbs in the background — matches ProfilePage's avatar mood */}
      <div className="pointer-events-none absolute -right-12 -top-16 h-64 w-64 rounded-full bg-brand/15 blur-3xl" />
      <div className="pointer-events-none absolute -left-16 -bottom-20 h-56 w-56 rounded-full bg-warning/10 blur-3xl" />

      <div className="relative px-6 sm:px-8 py-7 sm:py-9 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-medium text-fg-muted">
            <span>{formatLongDate()}</span>
            <Dot
              variant={isEmpty ? 'destructive' : isLow ? 'warning' : 'success'}
              pulse
            />
            <span>
              {isEmpty
                ? 'No credits — top up to keep scraping'
                : isLow
                ? 'Credits running low'
                : 'All systems normal'}
            </span>
          </div>

          <h1 className="mt-2 text-2xl sm:text-3xl font-semibold text-fg tracking-tight">
            {greeting}
            {fullName ? (
              <>
                , <span className="text-brand">{fullName}</span>
              </>
            ) : null}{' '}
            <span aria-label="waving hand" title="wave">👋</span>
          </h1>

          <p className="mt-1 text-sm text-fg-muted max-w-xl">
            {loading ? (
              <span className="inline-block h-4 w-48 rounded bg-surface-2 animate-pulse align-middle" />
            ) : hasRefill ? (
              <>
                You have <span className="text-fg font-medium tabular-nums">{formatNumber(credits)}</span> credits left this cycle.
              </>
            ) : (
              <>Here&apos;s how your API is doing.</>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Link to="/playground">
            <Button variant="primary" size="sm">
              <FlaskConical className="h-3.5 w-3.5" />
              Open Playground
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
          <a
            href="https://discord.gg/Gh6nuqkSe6"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-sm transition-colors whitespace-nowrap"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            <span>Join our Discord</span>
          </a>
        </div>
      </div>
    </div>
  );
}

function DaysWithUs({ user, billing }) {
  // Cozy stat — celebrates tenure with a milestone emoji and tiered copy.
  const createdAt = user?.created_at || billing?.subscription_start || null;
  const days = createdAt
    ? Math.max(1, Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000))
    : 0;
  const years = days >= 365 ? Math.floor(days / 365) : 0;
  const display = years > 0
    ? `${years} year${years === 1 ? '' : 's'}`
    : days > 0
      ? `${days} day${days === 1 ? '' : 's'}`
      : '—';

  // Milestone emoji — coarser tiers, max one tier at a time
  const emoji = (() => {
    if (days >= 730) return '🚀';      // 2+ years: legend
    if (days >= 365) return '🏆';      // 1+ year: veteran
    if (days >= 90)  return '🌳';      // 3+ months: mature
    if (days >= 30)  return '🌿';      // 1+ month: growing
    if (days >= 7)   return '🌱';      // 1+ week: sprouted
    return '✨';                        // brand-new
  })();

  const subtitle = years > 0
    ? `Customer since ${new Date(createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
    : days >= 7
      ? `Joined ${new Date(createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : 'Welcome — fresh on Datames';

  return (
    <Card size="sm" className="p-4 relative overflow-hidden">
      <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-warning/10 blur-xl" />
      <div className="relative flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-warning/15 border border-warning/30">
          <Cake className="h-4 w-4 text-warning" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-2xs font-medium text-fg-muted uppercase tracking-wider">
            With us
          </div>
          <div className="mt-1 text-xl font-semibold text-fg tabular-nums tracking-tight">
            {display}
          </div>
          <div className="text-2xs text-fg-subtle mt-0.5 truncate">
            {subtitle}
          </div>
        </div>
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-warning/10 text-2xl shrink-0 select-none"
          aria-hidden="true"
        >
          {emoji}
        </div>
      </div>
    </Card>
  );
}

function Calls30d({ totals, usage }) {
  // Cozy stat card — total calls + 7-day mini-bars + ok/err breakdown.
  // Color reflects activity: brand-tinted when active, muted when idle.
  const total = totals?.total || 0;
  const ok = totals?.totalOk || 0;
  const err = totals?.totalErrors || 0;
  const successRate = total > 0 ? (ok / total) * 100 : 100;

  // Last 7 days of usage (usage is ordered oldest → newest)
  const last7 = usage.slice(-7);
  const max7 = Math.max(1, ...last7.map((d) => (d.ok || 0) + (d.errors || 0)));

  // Color shifts from brand → warning → destructive as 7-day volume grows
  const heatClass = (n) => {
    const pct = n / max7;
    if (pct === 0) return 'bg-border';
    if (pct < 0.34) return 'bg-brand/40';
    if (pct < 0.67) return 'bg-brand/70';
    return 'bg-brand';
  };

  return (
    <Card size="sm" className="p-4 relative overflow-hidden">
      {/* Two-tone cozy backdrop — emerald-ish (success) + brand */}
      <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-success/15 blur-xl" />
      <div className="absolute -left-4 -bottom-4 h-16 w-16 rounded-full bg-brand/10 blur-xl" />
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <div className="text-2xs font-medium text-fg-muted uppercase tracking-wider">
            Calls (30d)
          </div>
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-success/15 border border-success/30">
            <Activity className="h-3.5 w-3.5 text-success" />
          </div>
        </div>

        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-semibold text-fg tabular-nums tracking-tight">
            {formatCompact(total)}
          </span>
          <span className="text-xs text-fg-muted">calls</span>
        </div>

        {/* 7-day mini-bars — only show if there's data */}
        {last7.length > 0 && total > 0 && (
          <div className="mt-2 flex items-end gap-1 h-8">
            {last7.map((d, i) => {
              const n = (d.ok || 0) + (d.errors || 0);
              const h = Math.max(2, (n / max7) * 28);
              return (
                <div
                  key={i}
                  className={`flex-1 rounded-t ${heatClass(n)}`}
                  style={{ height: `${h}px` }}
                  title={`${d.label}: ${n} calls`}
                />
              );
            })}
          </div>
        )}

        <div className="text-2xs text-fg-subtle mt-1.5 tabular-nums">
          {total > 0 ? (
            <>
              <span className="text-success">{formatNumber(ok)} ok</span>
              {' · '}
              <span className={err > 0 ? 'text-destructive' : 'text-fg-subtle'}>
                {formatNumber(err)} err
              </span>
              {successRate >= 99 ? ' · all green' : successRate < 95 ? ' · needs attention' : ''}
            </>
          ) : (
            'No calls yet'
          )}
        </div>
      </div>
    </Card>
  );
}

function DailySpark({ user, billing, stats }) {
  // Rotates by day. Pairs with the warm Hero — same cozy voice.
  const spark = getDailySpark(user?.created_at);
  const Icon = spark.icon;

  // Light context — figure out something the user has actually done
  // so the card feels personal, not just decorative.
  const calls = stats?.total || 0;
  const domain = stats?.topDomain;
  const planLabel = billing?.plan_display_name || billing?.plan_code;

  let context = '';
  if (calls > 0 && domain) {
    context = `You've made ${formatCompact(calls)} calls · most scraped ${domain}`;
  } else if (calls > 0) {
    context = `${formatCompact(calls)} calls served so far`;
  } else if (planLabel && planLabel !== 'Free') {
    context = `On the ${planLabel} plan — try the Playground when you're ready`;
  } else {
    context = 'Your first call is one click away →';
  }

  return (
    <Card size="sm" className="p-4 relative overflow-hidden">
      {/* Two-tone cozy backdrop — brand + warning blob like ProfilePage */}
      <div className="absolute -left-6 -top-6 h-24 w-24 rounded-full bg-brand/10 blur-xl" />
      <div className="absolute -right-8 -bottom-8 h-20 w-20 rounded-full bg-warning/10 blur-xl" />
      <div className="relative">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand/15 border border-brand/30">
            <Icon className="h-4 w-4 text-brand" />
          </div>
          <div className="min-w-0">
            <div className="text-2xs font-medium text-fg-muted uppercase tracking-wider">
              Daily spark
            </div>
            <p className="mt-1 text-xs text-fg leading-relaxed">
              {spark.text}
            </p>
            <p className="text-2xs text-fg-subtle mt-1.5 truncate">
              {context}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}

function RecentActivity({ logs, loading, onNavigate }) {
  return (
    <Card>
      <Card.Header>
        <div>
          <Card.Title>Recent activity</Card.Title>
          <Card.Subtitle>Your last 5 API calls.</Card.Subtitle>
        </div>
        <Link
          to="/usage"
          className="inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg transition-colors"
        >
          View all
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </Card.Header>
      <Card.Body className="p-0">
        {loading ? (
          <div className="px-5 py-3 space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : logs.length === 0 ? (
          <div className="px-5 py-10">
            <EmptyState
              icon={Activity}
              title="No calls yet"
              description="Hit the Playground to make your first request — it'll show up here."
            />
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {logs.map((l) => (
              <li key={l.id} className="flex items-center gap-3 px-5 py-3 text-sm">
                <span className="text-2xs text-fg-subtle tabular-nums w-20 shrink-0">
                  {l.occurred_at ? formatRelative(l.occurred_at) : '—'}
                </span>
                <div className="flex-1 min-w-0 truncate">
                  <span className="text-fg truncate">{l.domain || l.endpoint || 'request'}</span>
                </div>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 text-2xs px-1.5 py-0.5 rounded-md font-medium',
                    l.status === 'ok'
                      ? 'bg-success/10 text-success'
                      : 'bg-destructive/10 text-destructive',
                  )}
                >
                  {l.status === 'ok' ? (
                    <CheckCircle2 className="h-3 w-3" />
                  ) : (
                    <AlertTriangle className="h-3 w-3" />
                  )}
                  {l.status === 'ok' ? 'ok' : 'error'}
                </span>
                <span className="text-2xs text-fg-muted tabular-nums w-16 text-right">
                  {formatNumber(l.cost_credits || 0)} cr
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card.Body>
    </Card>
  );
}

function PlanCard({ billing, userCreatedAt }) {
  const planLabel = billing?.plan_display_name || billing?.plan_code || 'Free';
  const refill = billing?.initial_credits ?? 0;
  const credits = billing?.credit_balance ?? 0;
  const frozen = billing?.frozen_balance ?? 0;
  const used = refill > 0 ? Math.max(0, refill - credits) : 0;
  const usedPct = refill > 0 ? Math.min(1, used / refill) : 0;
  const overRateCents = billing?.over_usage_cents_per_1k_credits ?? 0;
  const rateLimit = billing?.rate_limit_per_minute ?? 1;
  const hardBlock = !!billing?.hard_block;
  const topupEligible = !!billing?.topup_eligible;
  const topupReason = billing?.topup_reason;
  const interval = billing?.billing_interval || 'monthly';

  // Renewal info (if any active sub)
  const subscriptionEnd = billing?.subscription_end;
  const graceUntil = billing?.grace_period_until;
  const renewsAt = subscriptionEnd ? new Date(subscriptionEnd) : null;
  const daysUntil = renewsAt
    ? Math.max(0, Math.ceil((renewsAt - new Date()) / (1000 * 60 * 60 * 24)))
    : null;
  const graceAt = graceUntil ? new Date(graceUntil) : null;
  const daysToForfeit = graceAt
    ? Math.max(0, Math.ceil((graceAt - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  // Days on this plan — derived from subscription_start, not user creation,
  // because the plan is what was bought, not just the account.
  const planDays = billing?.subscribed_date
    ? Math.max(1, Math.floor((Date.now() - new Date(billing.subscribed_date).getTime()) / 86400000))
    : 0;

  return (
    <Card className="relative overflow-hidden">
      {/* Top accent strip — brand color */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-brand via-brand/80 to-brand/60" />

      <Card.Header>
        <div>
          <Card.Title>Your plan</Card.Title>
          <Card.Subtitle>
            {interval === 'monthly' ? 'Monthly' : interval === 'quarterly' ? 'Quarterly' : 'No subscription'}
            {planDays > 0 && ` · day ${planDays}`}
          </Card.Subtitle>
        </div>
        <Badge variant="brand">{planLabel}</Badge>
      </Card.Header>

      <Card.Body className="space-y-5">
        {/* Donut: usage */}
        <div className="flex items-center justify-center pt-2">
          <DonutChart
            value={usedPct}
            size={140}
            strokeWidth={10}
            label={refill > 0 ? formatNumber(used) : '—'}
            sublabel={refill > 0 ? 'used this cycle' : 'no quota'}
            color={
              usedPct > 0.9
                ? 'hsl(var(--destructive))'
                : usedPct > 0.7
                  ? 'hsl(var(--warning))'
                  : 'hsl(var(--brand))'
            }
          />
        </div>

        {/* Stats rows */}
        <div className="space-y-2.5">
          <Row label="Cycle quota" value={formatNumber(refill)} />
          <Row label="Used" value={formatNumber(used)} />
          <Row label="Remaining" value={formatNumber(credits)} highlight />
          {frozen > 0 && (
            <Row
              label="Frozen (grace)"
              value={formatNumber(frozen)}
              hint="Frozen credits — renew to restore"
              warning
            />
          )}
        </div>

        {/* Pricing detail */}
        <div className="pt-2 border-t border-border space-y-1.5 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-fg-muted">Rate limit</span>
            <span className="font-medium tabular-nums">
              {rateLimit} request{rateLimit === 1 ? '' : 's'}/min
            </span>
          </div>
          {hardBlock ? (
            <div className="flex items-center justify-between">
              <span className="text-fg-muted">Overage policy</span>
              <span className="font-medium text-warning">Hard block (no overage)</span>
            </div>
          ) : overRateCents > 0 ? (
            <div className="flex items-center justify-between">
              <span className="text-fg-muted">Over-usage rate</span>
              <span className="font-medium tabular-nums">
                ${(overRateCents / 100).toFixed(2)} / 1k credits
              </span>
            </div>
          ) : null}
          {daysUntil != null && (
            <div className="flex items-center justify-between">
              <span className="text-fg-muted">Renews in</span>
              <span className="font-medium tabular-nums">
                {daysUntil} day{daysUntil === 1 ? '' : 's'}
              </span>
            </div>
          )}
          {daysToForfeit != null && (
            <div className="flex items-center justify-between">
              <span className="text-destructive">Forfeit in</span>
              <span className="font-medium tabular-nums text-destructive">
                {daysToForfeit} day{daysToForfeit === 1 ? '' : 's'}
              </span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="space-y-2 pt-2">
          {billing?.plan_code !== 'free' ? (
            <Link to="/billing" className="block">
              <Button variant="primary" size="sm" className="w-full">
                Change plan
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          ) : (
            <Link to="/billing" className="block">
              <Button variant="primary" size="sm" className="w-full">
                Upgrade plan
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          )}
          {topupEligible && (
            <Link to="/topup" className="block">
              <Button variant="ghost" size="sm" className="w-full">
                <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                Top up credits
              </Button>
            </Link>
          )}
          {!topupEligible && billing?.plan_code !== 'free' && topupReason === 'use_initial_first' && (
            <p className="text-2xs text-fg-subtle text-center">
              Top-up unlocks when credits run out.
            </p>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

function Row({ label, value, highlight = false, warning = false, hint }) {
  const valueClass = warning
    ? 'text-destructive font-semibold tabular-nums'
    : highlight
      ? 'text-fg font-semibold tabular-nums'
      : 'text-fg tabular-nums';
  return (
    <div className="flex items-center justify-between text-xs gap-2">
      <div className="flex flex-col min-w-0">
        <span className="text-fg-muted">{label}</span>
        {hint && <span className="text-2xs text-fg-subtle truncate">{hint}</span>}
      </div>
      <span className={valueClass}>{value}</span>
    </div>
  );
}

function QuickAction({ icon: Icon, title, description, to }) {
  return (
    <Link
      to={to}
      className="group block p-5 rounded-lg bg-surface border border-border hover:bg-surface-2 hover:border-border-strong transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-surface-2 border border-border group-hover:bg-brand/10 group-hover:border-brand/30 transition-colors">
          <Icon className="h-4 w-4 text-fg-muted group-hover:text-brand transition-colors" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-fg">{title}</div>
          <div className="text-xs text-fg-muted mt-0.5">{description}</div>
        </div>
        <ArrowUpRight className="h-3.5 w-3.5 text-fg-subtle group-hover:text-fg transition-colors mt-1" />
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function OverviewPage({ user, billing, onNavigate }) {
  const [usage, setUsage] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [loadingRecent, setLoadingRecent] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingUsage(true);
      try {
        const usageRes = await apiFetch('/payments/usage/daily?days=30').catch(() => null);
        if (cancelled) return;
        if (usageRes?.days) {
          setUsage(
            usageRes.days.map((d) => ({
              label: new Date(d.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              ok: d.ok || 0,
              errors: d.errors || 0,
            })),
          );
        }
      } finally {
        if (!cancelled) setLoadingUsage(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadRecent() {
      setLoadingRecent(true);
      try {
        const res = await apiFetch('/v1/logs?window=7d&limit=5').catch(() => null);
        if (cancelled) return;
        setRecent(res?.logs || []);
      } finally {
        if (!cancelled) setLoadingRecent(false);
      }
    }
    loadRecent();
    return () => { cancelled = true; };
  }, []);

  const totals = useMemo(() => {
    const total = usage.reduce((s, d) => s + d.ok + d.errors, 0);
    const totalOk = usage.reduce((s, d) => s + d.ok, 0);
    const totalErrors = usage.reduce((s, d) => s + d.errors, 0);
    const successRate = total > 0 ? (totalOk / total) * 100 : 100;
    return { total, totalOk, totalErrors, successRate };
  }, [usage]);

  const credits = billing?.credit_balance ?? 0;
  const refill = billing?.initial_credits ?? 0;
  const creditsPct = refill > 0 ? Math.min(100, (credits / refill) * 100) : 0;

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Hero greeting — warm, brand-tinted gradient */}
      <Hero user={user} billing={billing} loading={!billing} />

      {/* Renewal banner (T-7 / grace / expired) */}
      <RenewalBanner />

      {/* Stat row — 2 grid in 4 cols: Credits (full card) + 3 fun stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Card 1: Credits — the only number that matters, gets a full card */}
        <Card className="p-5 relative overflow-hidden">
          <div className="absolute -right-4 -top-4 h-32 w-32 rounded-full bg-brand/5 blur-2xl" />
          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-fg-muted uppercase tracking-wider">Credits</div>
              <Dot variant={credits > 0 ? 'success' : 'destructive'} pulse />
            </div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="text-3xl font-semibold text-fg tabular-nums">
                {formatNumber(credits)}
              </span>
              <span className="text-xs text-fg-muted">left</span>
            </div>
            {refill > 0 && (
              <>
                <div className="mt-3 h-1 bg-surface-2 rounded-full overflow-hidden">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-700',
                      creditsPct > 30 ? 'bg-brand' :
                      creditsPct > 15 ? 'bg-warning' :
                      'bg-destructive'
                    )}
                    style={{ width: `${creditsPct}%` }}
                  />
                </div>
                <div className="text-2xs text-fg-subtle flex justify-between mt-1.5">
                  <span>{Math.round(creditsPct)}% left</span>
                  <span>of {formatNumber(refill)}</span>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Stat 2: Calls (30d) — custom cozy card with mini-bars + breakdown */}
        <Calls30d totals={totals} usage={usage} />

        {/* Card 3: Days with us — cozy tenure badge (replaces Success Rate) */}
        <DaysWithUs user={user} billing={billing} />

        {/* Card 4: Daily spark — rotating cozy tip (replaces Status/Renewal) */}
        <DailySpark user={user} billing={billing} stats={totals} />
      </div>

      {/* Usage chart + Plan card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <Card.Header>
            <div>
              <Card.Title>API usage</Card.Title>
              <Card.Subtitle>Calls per day over the last 30 days.</Card.Subtitle>
            </div>
            <Badge variant="default">
              <Sparkles className="h-3 w-3 mr-0.5" />
              Realtime
            </Badge>
          </Card.Header>
          <Card.Body>
            {loadingUsage ? (
              <Skeleton className="h-48 w-full" />
            ) : usage.length > 0 ? (
              <AreaChart
                data={usage}
                series={[
                  { key: 'ok', label: 'Successful', color: 'hsl(var(--chart-1))' },
                  { key: 'errors', label: 'Errors', color: 'hsl(var(--chart-5))' },
                ]}
                height={240}
              />
            ) : (
              <EmptyState
                icon={Activity}
                title="No usage yet"
                description="Make your first API call to see usage here."
              />
            )}
          </Card.Body>
        </Card>

        <PlanCard billing={billing} userCreatedAt={user?.created_at} />
      </div>

      {/* Quick actions */}
      <div>
        <div className="text-xs font-medium text-fg-muted uppercase tracking-wider mb-3 px-1">
          Quick actions
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          <QuickAction
            icon={FlaskConical}
            title="Test a request"
            description="Open the Playground with a working snippet."
            to="/playground"
          />
          <QuickAction
            icon={KeyRound}
            title="Manage API keys"
            description="Create, rotate, or copy your active key."
            to="/keys"
          />
          <QuickAction
            icon={CreditCard}
            title="Top up credits"
            description="Buy credits without changing your plan."
            to="/billing"
          />
        </div>
      </div>

      {/* Recent activity */}
      <RecentActivity logs={recent} loading={loadingRecent} onNavigate={onNavigate} />
    </div>
  );
}
